import * as React from 'react'
import Joi, { Schema } from 'joi'
import { t } from 'i18next'
import { Dialog } from '@mui/material'
import { Close as CloseIcon, Search as SearchIcon, Inventory2Outlined as PackageIcon } from '@mui/icons-material'
import { useDispatch, useSelector } from '../../store'
import { ErrorsState, FormState, validateForm } from '../../function/validation'
import { checkPackageData, postPackage, putPackage } from '../../store/package/actionCreator'
import { clearFieldError } from '../../store/common/actionCreator'
import {
  Duration, IFeature, Package, PackageDomain, PackageType, SubtitleType, Workflow,
} from '../../store/package/reducer'
import { TableRef } from '../datatable/types'
import { PlanCard } from './PackagePreviewDialog'
import { UserPicker } from './UserPicker'
import l from '../../page/SubscribersPageV2.module.css'
import s from './PopupPackageV2.module.css'

/*
 * Create / edit a package — package_v2's dialog. The classic PopupPackage
 * (component/package/PopupPackage.tsx) is untouched and still serves /package;
 * this one keeps its fields, its rules and its requests field for field, and
 * changes how they are laid out: one form in sections on the left, the
 * package as the user will see it on the right, updating as you type.
 *
 * Validation is the classic form's (schemaBase / schemaFeatures, copied
 * below) with one change: the title is required — the classic form let a
 * package be saved without one. Features stay a soft check ("Save anyway"),
 * and an edited package's billing product id / uid still go through
 * /package/check before saving, as before.
 */

const DURATIONS: { id: Duration; label: string }[] = [
  { id: 1, label: '1 day' }, { id: 5, label: '5 days' }, { id: 15, label: '15 days' },
  { id: 30, label: '1 month' }, { id: 90, label: '3 months' }, { id: 180, label: '6 months' }, { id: 365, label: '12 months' },
]
const TYPES: { id: PackageType | 'one_time_fee'; label: string }[] = [
  { id: 'general', label: 'General' }, { id: 'personal', label: 'Personal' }, { id: 'custom', label: 'Customized' },
  { id: 'webinthub', label: 'IrbisPro' }, { id: 'telegrambt', label: 'Telegram BT' },
  { id: 'webinthub_subpackage', label: 'IrbisPro Subpackage' }, { id: 'webinthub_upgrade', label: 'IrbisPro Upgrade' },
  { id: 'demo', label: 'Demo (General)' }, { id: 'webinthub_demo', label: 'Demo (IrbisPro Personal)' },
  { id: 'one_time_fee', label: 'One Time Fee Package' },
]
const DOMAINS: { id: string; label: string }[] = [
  { id: 'irbis.espysys.com', label: 'irbis.espysys.com' }, { id: 'tools.clearcheck.io', label: 'tools.clearcheck.io' },
  { id: 'bgc.clearcheck.io', label: 'bgc.clearcheck.io' }, { id: 'bgc.telldata.io', label: 'bgc.telldata.io' },
  { id: 'bgc_core', label: 'bgc.kabat.ai' }, { id: 'espyosint.ddns.net', label: 'espyosint.ddns.net' },
]
const INFO_LIMIT = 100

/* ---- the classic form's schemas (PopupPackage.tsx), title made required ---- */
const schemaIsWebintUpgrade = Joi.any().when('type', {
  is: 'webinthub_upgrade', then: Joi.allow(null), otherwise: Joi.array().required().min(1),
})
const schemaIsFreeProductId = Joi.any().when('isFree', {
  is: true, then: Joi.allow(null), otherwise: Joi.number().required().min(10000).max(999999),
})
const schemaBase: Schema = Joi.object({
  type: Joi.allow('general', 'personal', 'custom', 'webinthub', 'telegrambt', 'webinthub_subpackage', 'demo', 'webinthub_demo', 'one_time_fee', 'webinthub_upgrade'),
  productId: Joi.any().label(t('field.label.productId')).when('type', {
    switch: [
      { is: 'custom', then: Joi.allow(null) },
      { is: 'personal', then: Joi.number().allow(null).min(10000).max(999999) },
    ],
    otherwise: schemaIsFreeProductId,
  }).concat(schemaIsFreeProductId),
  uid: Joi.string().label(t('field.label.uid')).when('type', {
    is: 'personal', then: Joi.string().alphanum().required().min(28).max(28),
  }).concat(Joi.string().when('type', {
    is: 'webinthub_demo', then: Joi.string().alphanum().required().min(28).max(28), otherwise: Joi.allow(null),
  })),
  apiRequestsLimits: Joi.number().min(0).label(t('field.label.APIlimits'))
    .when('type', { is: 'personal', then: Joi.number(), otherwise: Joi.allow(null) }),
  apiRequestsInterval: Joi.number().min(0).label(t('field.label.APIinterval'))
    .when('type', { is: 'personal', then: Joi.number(), otherwise: Joi.allow(null) }),
  // the one change: a package must have a title
  title: Joi.string().trim().required().label(t('field.label.title')),
  subTitleType: Joi.string(),
  subTitleContext: Joi.string().label(t('package.subtitle')),
  lookupIds: Joi.any().label(t('field.label.searchWorkflow'))
    .when('type', { is: 'custom', then: Joi.allow(null), otherwise: schemaIsWebintUpgrade }),
  price: Joi.number().required().label(t('field.label.price'))
    .when('type', { is: 'custom', then: Joi.allow(null), otherwise: Joi.number().min(0) })
    .concat(Joi.number().when('isFree', { is: true, then: Joi.allow(0), otherwise: Joi.number().min(0) })),
  bonus: Joi.number().min(0).label(t('field.label.bonus')).when('type', { is: 'custom', then: Joi.allow(null) }),
  creditsPerUnit: Joi.number().min(0).required().label(t('field.label.creditsPerUnit'))
    .when('type', { is: 'general', then: Joi.number().min(0) })
    .concat(Joi.number().when('type', { is: 'personal', then: Joi.number().min(0), otherwise: Joi.allow(null) })),
  annualPrice: Joi.number().min(0).label(t('field.label.annualPrice')).when('isAnnual', {
    is: true,
    then: Joi.number().integer()
      .min(Joi.ref('price', { render: true }))
      .max(Joi.ref('price', { adjust: (v) => v * 12, render: true }))
      .required(),
  }),
  annualProductId: Joi.number().integer().min(0).label(t('field.label.annualPackageId'))
    .when('isAnnual', { is: true, then: Joi.number().integer().required() }),
  isFree: Joi.boolean(),
  isAnnual: Joi.boolean(),
  isActive: Joi.boolean(),
  isIrbisPro: Joi.boolean(),
  isBestProposition: Joi.boolean(),
  isAiAssistant: Joi.boolean(),
  searchRuleType: Joi.boolean(),
  getRequestsCount: Joi.number(),
  maxKeysNum: Joi.number().integer().min(0).default(0),
  activeDay: Joi.number().label(t('field.label.duration'))
    .when('type', { is: 'custom', then: Joi.allow(null) })
    .concat(Joi.number().when('type', { is: 'webinthub_demo', then: Joi.allow(null), otherwise: Joi.number().required() })),
  button: Joi.string().label(t('field.label.button')).when('isFree', { is: true, then: Joi.allow(null, '') }),
  demoLink: Joi.string().when('type', { is: 'custom', then: Joi.string(), otherwise: Joi.allow(null) }),
  packageDomain: Joi.allow(null),
})
const schemaFeatures: Schema = Joi.object({
  features: Joi.array().required().items(Joi.object({ title: Joi.string(), subTitle: Joi.string(), info: Joi.string() })),
})

/* ---- small pieces ---- */
function Toggle({ on, onChange, disabled, label, hint }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string; hint?: string }) {
  return (
    <button type="button" className={`${s.toggle} ${on ? s.toggleOn : ''}`} disabled={disabled} onClick={() => onChange(!on)} aria-pressed={on}>
      <span className={s.switch}><span /></span>
      <span className={s.toggleText}>
        <b>{label}</b>
        {hint && <span>{hint}</span>}
      </span>
    </button>
  )
}

function Field({ label, required, error, hint, children }: { label: string; required?: boolean; error?: React.ReactNode; hint?: string; children: React.ReactNode }) {
  const err = error && typeof error === 'object' ? Object.values(error as Record<string, string>).join(' · ') : error
  return (
    <label className={s.field}>
      <span className={s.label}>{label}{required && <i>*</i>}</span>
      {children}
      {err ? <span className={s.err}>{err}</span> : hint ? <span className={s.hint}>{hint}</span> : null}
    </label>
  )
}

function Section({ n, title, sub, children }: { n: number; title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section className={s.section}>
      <div className={s.sectionHead}>
        <span className={s.num}>{n}</span>
        <div><b>{title}</b>{sub && <span>{sub}</span>}</div>
      </div>
      <div className={s.sectionBody}>{children}</div>
    </section>
  )
}

type Props = {
  mode: 'create' | 'edit'
  tableRef: React.RefObject<TableRef>
  onClose: () => void
  data?: Package
  theme: 'light' | 'dark'
}

export function PopupPackageV2({ mode, tableRef, onClose, data, theme }: Props) {
  const dispatch = useDispatch()
  const { tables, fieldErrors } = useSelector((state) => state.common)
  const allWorkflows: Workflow[] = tables.workflow?.list || []

  const [type, setType] = React.useState<PackageType>(data?.type || 'general')
  const [packageDomain, setPackageDomain] = React.useState<PackageDomain>(data?.packageDomain || 'irbis.espysys.com')
  const [productId, setProductId] = React.useState<number | string>(data?.productId || '')
  const [uid, setUid] = React.useState<string>(data?.uid || '')
  const [title, setTitle] = React.useState(data?.title || '')
  const [subTitleType, setSubTitleType] = React.useState<SubtitleType>(data?.subTitle?.type || 'text')
  const [subTitleContext, setSubTitleContext] = React.useState<string>(data?.subTitle?.context || '')
  const [lookups, setLookups] = React.useState<Workflow[]>(() => {
    const ids = new Set((data?.lookups || []).map((x) => x.id))
    return allWorkflows.filter((w) => ids.has(w.id))
  })
  const [apiRequestsLimits, setApiRequestsLimits] = React.useState<number>(data?.apiRequestsLimits || 1)
  const [apiRequestsInterval, setApiRequestsInterval] = React.useState<number>(data?.apiRequestsInterval || 1)
  const [features, setFeatures] = React.useState<IFeature[]>(() => [data?.feature1, data?.feature2, data?.feature3, data?.feature4]
    .map((f) => ({ title: f?.title || '', subTitle: f?.subTitle || '', info: f?.info || '' })))
  const [price, setPrice] = React.useState<number>(data?.price || 0)
  const [bonus, setBonus] = React.useState<number>(data?.bonus || 0)
  const [creditsPerUnit, setCreditsPerUnit] = React.useState<number>(data?.creditsPerUnit || 0)
  const [isFree, setIsFree] = React.useState(data?.isFree || false)
  const [isAnnual, setIsAnnual] = React.useState(data?.isAnnual || false)
  const [annualProductId, setAnnualProductId] = React.useState<number>(data?.annualProductId || 0)
  const [annualPrice, setAnnualPrice] = React.useState<number>(data?.annualPrice || 0)
  // a new package is active, as the server's default is (the classic form sent nothing and got that)
  const [isActive, setIsActive] = React.useState(data ? !!data.isActive : true)
  const [isIrbisPro, setIrbisPro] = React.useState(!!data?.isIrbisPro)
  const [isBestProposition, setBestProposition] = React.useState(!!data?.isBestProposition)
  const [isAiAssistant, setIsAiAssistant] = React.useState(!!data?.isAiAssistant)
  const [searchRuleType, setSearchRuleType] = React.useState(!!data?.searchRuleType)
  const [getRequestsCount, setEvaluationCount] = React.useState<number>(data?.getRequestsCount || 0)
  const [maxKeysNum, setMaxKeysNum] = React.useState<number>(data?.maxKeysNum ?? 0)
  const [multipleApiKeys, setMultipleApiKeys] = React.useState((data?.maxKeysNum ?? 0) > 0)
  const [activeDay, setActiveDay] = React.useState<Duration | null>(data?.activeDay || null)
  const [button, setButton] = React.useState<string>(data?.button || '')
  const [demoLink, setDemoLink] = React.useState<string>(data?.demoLink || '')
  const [errors, setErrors] = React.useState<ErrorsState>(null)
  const [errorsFeatures, setErrorsFeatures] = React.useState<ErrorsState>(null)
  const [askSaveAnyway, setAskSaveAnyway] = React.useState(false)
  const [wfQuery, setWfQuery] = React.useState('')
  const [wfOpen, setWfOpen] = React.useState(false)
  const [previewAnnual, setPreviewAnnual] = React.useState(true)
  const formRef = React.useRef<HTMLDivElement>(null)

  // any edit clears the shown errors, as in the classic form
  React.useEffect(() => {
    setErrors(null)
    dispatch(clearFieldError)
  }, [dispatch, type, packageDomain, productId, uid, title, subTitleContext, features, price, bonus, creditsPerUnit, isFree, isAnnual,
    activeDay, button, isActive, isIrbisPro, isBestProposition, isAiAssistant, searchRuleType, getRequestsCount, maxKeysNum,
    annualProductId, annualPrice, demoLink, lookups])

  const isDemo = type === 'demo'
  const isCustom = type === 'custom'
  const isPersonal = type === 'personal'
  const isWebintHub = type === 'webinthub'
  const isWebintSubPackage = type === 'webinthub_subpackage'
  const isWebintHubUpgrade = type === 'webinthub_upgrade'
  const isWebintHubDemo = type === 'webinthub_demo'
  const isOneTimeFee = (type as string) === 'one_time_fee'
  const isGeneral = type === 'general'
  const useUID = isWebintHubDemo || isPersonal

  const packages: Package[] = tables?.package?.list || []
  const alreadyHaveCustom = packages.some((el) => el.type === 'custom' && el.id !== data?.id)
  const alreadyHaveDemo = packages.some((el) => el.type === 'demo' && el.id !== data?.id)

  const shownFeatures = isCustom ? features : features.slice(0, 3)
  const lookupIds = isCustom ? null : lookups.map((el) => el.id)
  const productIdIsNull = isCustom || isFree || isDemo || productId === ''

  // which workflows the type offers (the classic form's three pickers)
  const workflowFilter = (w: Workflow): boolean => {
    if (isWebintSubPackage) return w.type === 'additional_quotas'
    if (isWebintHub || isWebintHubDemo) return w.type === 'webint'
    return w.type !== 'webint'
  }
  const showWorkflows = !isCustom && !isWebintHubUpgrade
  const selectedIds = new Set(lookups.map((w) => w.id))
  const q = wfQuery.trim().toLowerCase()
  const offered = allWorkflows.filter((w) => !selectedIds.has(w.id) && workflowFilter(w) && (!q || w.title.toLowerCase().includes(q)))

  const requestData: FormState = {
    type,
    packageDomain: isCustom || isFree || isDemo ? null : packageDomain,
    subTitleType,
    lookupIds,
    isFree,
    isAnnual,
    activeDay,
    title: title.trim(),
    subTitleContext: subTitleContext.trim(),
    uid: useUID ? uid : null,
    apiRequestsLimits: isPersonal ? apiRequestsLimits : 0,
    apiRequestsInterval: isPersonal ? apiRequestsInterval : 0,
    productId: productIdIsNull ? null : productId,
    price: isCustom ? null : price,
    bonus: isCustom ? null : bonus,
    creditsPerUnit: isPersonal || isGeneral ? creditsPerUnit : null,
    button: isFree || isDemo ? null : button.trim(),
    demoLink: isCustom ? demoLink : null,
    annualPrice: isAnnual ? annualPrice : 0,
    annualProductId: isAnnual ? annualProductId : 0,
    isActive,
    isIrbisPro,
    isBestProposition,
    isAiAssistant,
    searchRuleType,
    getRequestsCount,
    maxKeysNum: isPersonal ? maxKeysNum : 0,
  }

  // the classic form's dependent switches
  const changeType = (v: PackageType) => { setType(v); setIsFree(false); setIsAnnual(false) }
  const changeFree = (v: boolean) => { setIsFree(v); setPrice(v ? 0 : 1) }
  const changeAnnual = (v: boolean) => { setIsAnnual(v); setAnnualPrice(v ? 0 : 1); setAnnualProductId(v ? 0 : 1) }
  const disabledAnnual = isDemo || isWebintHubUpgrade || isWebintSubPackage || isCustom || isWebintHubDemo || isOneTimeFee
  const disabledSearchRules = !(isGeneral || isPersonal || isOneTimeFee)
  const disabledEvaluation = !(isGeneral || isPersonal || isOneTimeFee)

  const setFeature = (i: number, key: keyof IFeature, value: string) =>
    setFeatures((list) => list.map((f, idx) => (idx === i ? { ...f, [key]: key === 'info' ? value.slice(0, INFO_LIMIT) : value } : f)))

  function submit() {
    const body: FormState = { ...requestData }
    body.subTitle = { type: body.subTitleType, context: body.subTitleContext }
    shownFeatures.forEach((el, idx) => { body[`feature${idx + 1}`] = el })
    delete body.subTitleContext
    delete body.subTitleType
    delete body.features
    const done = () => { tableRef.current?.refresh(); onClose() }
    if (mode === 'create') dispatch(postPackage(body, done))
    else dispatch(putPackage(body, data?.id as number, done))
  }

  function afterCheck() {
    const featureErrors = validateForm(schemaFeatures, { features: shownFeatures }, true)
    setErrorsFeatures(featureErrors || null)
    if (featureErrors) setAskSaveAnyway(true)
    else submit()
  }

  function save() {
    const base = validateForm(schemaBase, requestData)
    if (base) {
      setErrors(base)
      // bring the first field with an error into view
      requestAnimationFrame(() => formRef.current?.querySelector(`.${s.err}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }))
      return
    }
    setErrors(null)
    if (typeof productId === 'number' && mode === 'edit') {
      dispatch(checkPackageData(productId, uid, apiRequestsLimits, apiRequestsInterval, afterCheck))
    } else {
      afterCheck()
    }
  }

  const err = (key: string) => (fieldErrors as any)?.[key] || errors?.[key]
  const num = (v: string) => (v === '' ? 0 : Number(v))

  // the card as the user will see it, from what is typed right now
  const preview = {
    ...(data || {}),
    id: data?.id ?? 0,
    type,
    title: title || 'Untitled package',
    subTitle: subTitleContext ? { type: subTitleType, context: subTitleContext } : null,
    price: isCustom ? null : price,
    annualPrice: isAnnual ? annualPrice : null,
    annualProductId: isAnnual ? (annualProductId || 1) : null,
    creditsPerUnit,
    activeDay,
    isFree,
    isBestProposition,
    button: button || 'Buy',
    feature1: features[0], feature2: features[1], feature3: features[2], feature4: features[3],
  } as unknown as Package

  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2, maxHeight: 'calc(100vh - 32px)' } }}>
      <div className={`${l.theme} ${s.shell}`} data-theme={theme}>
        <div className={s.top}>
          <span className={s.icon}><PackageIcon sx={{ fontSize: 19 }} /></span>
          <div className={s.topText}>
            <b>{mode === 'create' ? 'Create package' : `Edit package: ${data?.title || ''}`}</b>
            <span>{mode === 'create' ? 'Fill in the sections — the card on the right is what users will see.' : 'Changes show in the card on the right before you save.'}</span>
          </div>
          <button type="button" className={s.close} onClick={onClose} aria-label="Close"><CloseIcon sx={{ fontSize: 18 }} /></button>
        </div>

        <div className={s.main}>
          <div className={s.form} ref={formRef}>
            <Section n={1} title="Type and billing" sub="What kind of package it is and where it is sold">
              <div className={s.grid2}>
                <Field label="Type">
                  <select className={s.input} value={type} disabled={mode === 'edit'} onChange={(e) => changeType(e.target.value as PackageType)}>
                    {TYPES.map((o) => (
                      <option key={o.id} value={o.id} disabled={(o.id === 'custom' && alreadyHaveCustom) || (o.id === 'demo' && alreadyHaveDemo)}>{o.label}</option>
                    ))}
                  </select>
                </Field>
                {!isCustom && !isFree && !isDemo && (
                  <Field label="Domain">
                    <select className={s.input} value={packageDomain} onChange={(e) => setPackageDomain(e.target.value as PackageDomain)}>
                      {DOMAINS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </Field>
                )}
              </div>
              {!isCustom && !isFree && !isDemo && (
                <Field
                  label={isPersonal ? 'PayPro product ID (skip for Enterprise)' : 'PayPro product ID'}
                  required={!isPersonal}
                  error={err('productId')}
                  hint="5–6 digits, the product in PayPro"
                >
                  <input className={`${s.input} ${s.mono}`} type="number" placeholder="12345" value={productId}
                    onChange={(e) => setProductId(e.target.value === '' ? '' : Number(e.target.value))} />
                </Field>
              )}
              {useUID && (
                <Field label="Owner" required error={err('uid')} hint="The user this package belongs to">
                  <UserPicker value={uid} onChange={(v) => setUid(v.trim())} invalid={!!err('uid')} />
                </Field>
              )}
              {isPersonal && (
                <div className={s.grid2}>
                  <Field label="API requests limit" error={err('apiRequestsLimits')}>
                    <input className={s.input} type="number" value={apiRequestsLimits} onChange={(e) => setApiRequestsLimits(num(e.target.value))} />
                  </Field>
                  <Field label="API requests interval" error={err('apiRequestsInterval')}>
                    <input className={s.input} type="number" value={apiRequestsInterval} onChange={(e) => setApiRequestsInterval(num(e.target.value))} />
                  </Field>
                </div>
              )}
            </Section>

            <Section n={2} title="Card content" sub="Title, subtitle and the buy button">
              <Field label="Title" required error={err('title')}>
                <input className={s.input} value={title} placeholder={String(t('field.hint.title'))} onChange={(e) => setTitle(e.target.value)} />
              </Field>
              <Field label="Subtitle" required error={err('subTitleContext')}>
                <div className={s.inline}>
                  <div className={s.seg}>
                    {(['text', 'status'] as SubtitleType[]).map((v) => (
                      <button key={v} type="button" className={`${s.segBtn} ${subTitleType === v ? s.segOn : ''}`} onClick={() => setSubTitleType(v)}>
                        {v === 'text' ? 'Text' : 'Status chip'}
                      </button>
                    ))}
                  </div>
                  <input className={s.input} value={subTitleContext} placeholder="Shown under the title" onChange={(e) => setSubTitleContext(e.target.value)} />
                </div>
              </Field>
              {(isCustom || (!isFree && !isDemo)) && (
                <Field label="Button text" required error={err('button')}>
                  <input className={s.input} value={button} placeholder="Buy now" onChange={(e) => setButton(e.target.value)} />
                </Field>
              )}
              {isCustom && (
                <Field label="Demo link" required error={err('demoLink')}>
                  <input className={s.input} value={demoLink} placeholder="https://…" onChange={(e) => setDemoLink(e.target.value)} />
                </Field>
              )}
            </Section>

            {!isCustom && (
              <Section n={3} title="Price and period" sub="What the user pays, for how long, and the annual option">
                {!isWebintHubDemo && (
                  <>
                    <div className={s.grid3}>
                      <Field label="Price, €" required error={err('price')}>
                        <input className={s.input} type="number" min={0} value={price} readOnly={isFree} onChange={(e) => setPrice(num(e.target.value))} />
                      </Field>
                      <Field label="Bonus, €" error={err('bonus')}>
                        <input className={s.input} type="number" min={0} value={bonus} onChange={(e) => setBonus(num(e.target.value))} />
                      </Field>
                      {(isGeneral || isPersonal) && (
                        <Field label="Credits per €" required error={err('creditsPerUnit')}>
                          <input className={s.input} type="number" min={0} value={creditsPerUnit} onChange={(e) => setCreditsPerUnit(num(e.target.value))} />
                        </Field>
                      )}
                    </div>
                    {!isPersonal && !isOneTimeFee && (
                      <Toggle on={isFree} onChange={changeFree} disabled={!!data?.isFree} label="Free (welcome package)" hint="Price 0; what new sign-ups get" />
                    )}
                  </>
                )}
                <Field label="Period" required error={err('activeDay')}>
                  <div className={s.chips}>
                    {DURATIONS.map((d) => (
                      <button key={d.id} type="button" className={`${s.chip} ${activeDay === d.id ? s.chipOn : ''}`} onClick={() => setActiveDay(d.id)}>{d.label}</button>
                    ))}
                  </div>
                </Field>
                <Toggle on={isAnnual} onChange={changeAnnual} disabled={disabledAnnual} label="Annual plan" hint="Sold per year as a separate PayPro product" />
                {isAnnual && (
                  <div className={s.grid2}>
                    <Field label="Annual price, €" required error={err('annualPrice')} hint={`Between ${price} and ${price * 12}`}>
                      <input className={s.input} type="number" min={0} value={annualPrice} onChange={(e) => setAnnualPrice(num(e.target.value))} />
                    </Field>
                    <Field label="Annual PayPro product ID" required error={err('annualProductId')}>
                      <input className={`${s.input} ${s.mono}`} type="number" value={annualProductId} onChange={(e) => setAnnualProductId(num(e.target.value))} />
                    </Field>
                  </div>
                )}
              </Section>
            )}

            {showWorkflows && (
              <Section n={isCustom ? 3 : 4} title="Search workflows" sub={isWebintSubPackage ? 'Additional quotas' : isWebintHub || isWebintHubDemo ? 'Irbis Pro workflows' : 'What the package lets the user run'}>
                <Field label="Workflows" required error={err('lookupIds')}>
                  <div className={s.wf}>
                    {lookups.length > 0 && (
                      <div className={s.wfChips}>
                        {lookups.map((w) => (
                          <span key={w.id} className={s.wfChip} style={{ borderColor: w.color || undefined }}>
                            <i style={{ background: w.color || 'var(--primary)' }} />
                            {w.title}
                            <button type="button" onClick={() => setLookups((list) => list.filter((x) => x.id !== w.id))} aria-label={`Remove ${w.title}`}>×</button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className={s.wfSearch}>
                      <SearchIcon sx={{ fontSize: 17, color: 'var(--text3)' }} />
                      <input value={wfQuery} placeholder="Add a workflow" onFocus={() => setWfOpen(true)} onChange={(e) => { setWfQuery(e.target.value); setWfOpen(true) }} />
                      {wfOpen && <button type="button" className={s.wfDone} onClick={() => setWfOpen(false)}>Done</button>}
                    </div>
                    {wfOpen && (
                      <div className={s.wfList}>
                        {offered.length ? offered.map((w) => (
                          <button key={w.id} type="button" className={s.wfItem} onClick={() => setLookups((list) => [...list, w])}>
                            <i style={{ background: w.color || 'var(--primary)' }} />
                            <span>{w.title}</span>
                            <small>{w.subTitle}</small>
                          </button>
                        )) : <div className={s.wfNone}>{q ? `No workflows match “${wfQuery.trim()}”` : 'All workflows are added'}</div>}
                      </div>
                    )}
                  </div>
                </Field>
              </Section>
            )}

            <Section n={(isCustom ? 3 : 4) + (showWorkflows ? 1 : 0)} title="Features" sub="The checkmark lines on the card">
              <div className={s.features}>
                {shownFeatures.map((f, i) => {
                  const fe = (errorsFeatures as any)?.[`features${i}`]
                  return (
                    <div key={i} className={`${s.feature} ${fe ? s.featureErr : ''}`}>
                      <span className={s.featureNum}>{i + 1}</span>
                      <div className={s.featureFields}>
                        <input className={s.input} value={f.title} placeholder="Title" onChange={(e) => setFeature(i, 'title', e.target.value)} onBlur={(e) => setFeature(i, 'title', e.target.value.trim())} />
                        <input className={s.input} value={f.subTitle} placeholder="Subtitle" onChange={(e) => setFeature(i, 'subTitle', e.target.value)} onBlur={(e) => setFeature(i, 'subTitle', e.target.value.trim())} />
                        <div className={s.infoWrap}>
                          <textarea className={`${s.input} ${s.textarea}`} value={f.info} placeholder="Info (tooltip)" rows={2} onChange={(e) => setFeature(i, 'info', e.target.value)} />
                          <span className={s.counter}>{f.info.length}/{INFO_LIMIT}</span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </Section>

            <Section n={(isCustom ? 4 : 5) + (showWorkflows ? 1 : 0)} title="Options">
              <div className={s.toggles}>
                <Toggle on={isActive} onChange={setIsActive} disabled={isDemo} label="Active" hint="Shown to users" />
                <Toggle on={isBestProposition} onChange={setBestProposition} label="Best offer" hint="Highlighted card" />
                <Toggle on={isIrbisPro} onChange={setIrbisPro} label="Irbis PRO" hint="Gives access to Irbis Pro" />
                <Toggle on={isAiAssistant} onChange={setIsAiAssistant} label="AI Assistant" hint="IRBI chat included" />
                <Toggle on={searchRuleType} onChange={setSearchRuleType} disabled={disabledSearchRules} label="Show results from DB" />
                <div className={s.toggleRow}>
                  <Toggle on={getRequestsCount > 0} onChange={(v) => setEvaluationCount(v ? 1 : 0)} disabled={disabledEvaluation} label="Evaluation" hint="Requests to try" />
                  {getRequestsCount > 0 && (
                    <input className={`${s.input} ${s.small}`} type="number" value={getRequestsCount} onChange={(e) => setEvaluationCount(num(e.target.value))} />
                  )}
                </div>
                {isPersonal && (
                  <div className={s.toggleRow}>
                    <Toggle
                      on={multipleApiKeys}
                      onChange={(v) => { setMultipleApiKeys(v); setMaxKeysNum(v ? (maxKeysNum === 0 ? 1 : maxKeysNum) : 0) }}
                      label="Multiple API keys"
                      hint="Max keys"
                    />
                    {multipleApiKeys && (
                      <input className={`${s.input} ${s.small}`} type="number" value={maxKeysNum} onChange={(e) => setMaxKeysNum(num(e.target.value))} />
                    )}
                  </div>
                )}
              </div>
            </Section>
          </div>

          <aside className={s.preview}>
            <div className={s.previewHead}>
              <span>Preview</span>
              <div className={s.seg}>
                <button type="button" className={`${s.segBtn} ${!previewAnnual ? s.segOn : ''}`} onClick={() => setPreviewAnnual(false)}>Monthly</button>
                <button type="button" className={`${s.segBtn} ${previewAnnual ? s.segOn : ''}`} onClick={() => setPreviewAnnual(true)}>Annual</button>
              </div>
            </div>
            <div className={s.previewStage}>
              <PlanCard p={preview} annual={previewAnnual} />
            </div>
            {!isActive && <div className={s.previewNote}>Inactive — users will not see it until it is switched on.</div>}
          </aside>
        </div>

        <div className={s.foot}>
          {errors && <span className={s.footErr}>Some fields need attention</span>}
          <button type="button" className={s.ghost} onClick={onClose}>Cancel</button>
          <button type="button" className={s.primary} onClick={save}>{mode === 'create' ? 'Create package' : 'Save changes'}</button>
        </div>

        {askSaveAnyway && (
          <div className={s.confirm}>
            <div className={s.confirmBox}>
              <b>Some feature fields aren&apos;t filled</b>
              <span>The card will show them empty. Save anyway?</span>
              <div className={s.confirmBtns}>
                <button type="button" className={s.ghost} onClick={() => setAskSaveAnyway(false)}>Back to form</button>
                <button type="button" className={s.primary} onClick={() => { setAskSaveAnyway(false); submit() }}>Save anyway</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
}
